import {
  ErrorAlert,
  Heading_4 as Heading4,
  Loader,
  useThemeContext,
} from '@huggg/react-components';
import { CreationState } from 'components/ui/PrepaidCard/CreationState';
import dictionary from 'config/dictionary';
import PropTypes from 'prop-types';
import { compose, path, pathOr } from 'ramda';
import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { getEffectiveSegment } from 'redux/modules/huggg';
import {
  fetchCardData,
  fetchSensitiveCardData,
  reportMisuse,
  startStatusPolling,
  stopStatusPolling,
  getAvailableBalanceStr,
  getCardData,
  getCardStatus,
  getFetchCardDataError,
  getFetchSensitiveCardDataError,
  getIsFetchingCardData,
  getIsFetchingSensitiveCardData,
  getIsStatusPollExhausted,
  getSensitiveCardData,
  getSpentAmountStr,
  getIsReportingMisuse,
  getReportMisuseError,
} from 'redux/modules/prepaidCard';
import { isPrepaidHeatingCard } from 'utilities/huggg';
import { isValidExternalLink } from 'utilities/links';
import { getKnownProviderErrors } from 'utilities/prepaidProviderErrors';
import CancelledCard from './CancelledCard';
import CardImage from './CardImage';
import CardInformation from './CardInformation';
import { CardRestrictionDetails } from './CardRestrictionDetails';
import styles from './prepaidCard.module.scss';

const getCardTitle = (segment) => {
  return pathOr(
    'Card',
    ['configuration', 'sangriaLayout', 'prepaidCard', 'title'],
    segment,
  );
};

const legacyHelpLinks = {
  energy: {
    href: dictionary.prepaidCards.links.energyCardHelp,
    label: dictionary.prepaidCards.labels.energyCardHelpLabel,
  },
  water: {
    href: dictionary.prepaidCards.links.waterCardHelp,
    label: dictionary.prepaidCards.labels.waterCardHelpLabel,
  },
};

export const getHelpLink = (segment, cardTitle) => {
  const configuredHelpLink = path(
    ['configuration', 'sangriaLayout', 'prepaidCard', 'helpLink'],
    segment,
  );

  if (configuredHelpLink !== undefined) {
    return isValidExternalLink(configuredHelpLink) ? configuredHelpLink : null;
  }

  const keyword = ['energy', 'water'].find((candidate) =>
    cardTitle.toLowerCase().includes(candidate),
  );

  return legacyHelpLinks[keyword] || null;
};

export const getCardDesign = (segment) => {
  return (
    path(
      ['configuration', 'sangriaLayout', 'prepaidCard', 'design'],
      segment,
    ) || null
  );
};

export const PrepaidCardUnconnected = (props) => {
  const { operator } = useThemeContext();
  const {
    id,
    isFetchingCardData,
    fetchCardData,
    fetchCardDataError,
    availableBalance,
    spentAmount,
    sensitiveCardData,
    isFetchingSensitiveCardData,
    fetchSensitiveCardDataError,
    fetchSensitiveCardData,
    reportMisuse,
    isReportingMisuse,
    reportMisuseError,
    cardData,
    cardStatus,
    isStatusPollExhausted,
    startStatusPolling,
    stopStatusPolling,
    segment,
    shortcode,
    isHeatingCard,
  } = props;

  const cardTitle = getCardTitle(segment);
  const cardDesign = getCardDesign(segment);

  const helpLinkToShow = useMemo(
    () =>
      operator.showHugggSupportLinks ? getHelpLink(segment, cardTitle) : null,
    [cardTitle, segment, operator.showHugggSupportLinks],
  );

  const declineMessage = path(
    ['configuration', 'sangriaLayout', 'prepaidCard', 'declineMessage'],
    segment,
  );
  const cardDetailsMessage = path(
    ['configuration', 'sangriaLayout', 'prepaidCard', 'cardDetailsMessage'],
    segment,
  );
  const knownProviderErrors = useMemo(
    () => getKnownProviderErrors(shortcode, declineMessage, operator),
    [shortcode, declineMessage, operator],
  );

  useEffect(() => {
    fetchCardData(id);
  }, [fetchCardData, id]);

  // The status poll is the fresher signal once it has run; the full card is
  // what the page first loads with and what the balance panel renders from.
  const status = cardStatus?.status ?? cardData?.status;
  const fundingStatus = cardStatus?.fundingStatus ?? cardData?.fundingStatus;
  const creationFailure = cardStatus?.creationFailure ?? null;
  const isPendingCreation = status === 'pending-creation';
  const isPendingFunding = fundingStatus === 'pending';

  // Creation runs on a capped provider queue and funding confirms by webhook
  // after it: poll the status route while either is outstanding. The poll
  // refreshes the full card when it settles, which is what ends this state.
  const shouldPollStatus =
    Boolean(cardData) &&
    !creationFailure &&
    (isPendingCreation || isPendingFunding);

  useEffect(() => {
    if (!shouldPollStatus) {
      return undefined;
    }

    startStatusPolling(id);

    return () => stopStatusPolling();
  }, [shouldPollStatus, id, startStatusPolling, stopStatusPolling]);

  // Only block on the loader / surface a fetch error before the first card
  // fetch has succeeded: during a poll the stale card stays on screen rather
  // than flickering, and a failed background poll must not replace the
  // pending state with an error.
  if (isFetchingCardData && !cardData) {
    return (
      <div className={styles.container}>
        <Loader />
      </div>
    );
  }

  if ((fetchCardDataError && !cardData) || status === 'pending') {
    return (
      <div className={styles.container}>
        <ErrorAlert message="There was an error fetching your card, please refresh and try again." />
      </div>
    );
  }

  if (!cardData) {
    return (
      <div className={styles.container}>
        <CancelledCard />
      </div>
    );
  }

  // No CardRestrictionDetails in either waiting state: usage guidance (e.g.
  // "How do I use this digital card?") only makes sense once the card is
  // ready to use.
  const renderWaitingState = (content) => (
    <div className={styles.container}>
      <CardImage
        cardData={cardData}
        cardTitle={cardTitle}
        design={cardDesign}
        isHeatingCard={isHeatingCard}
      />

      <div className={styles.fundingState}>{content}</div>
    </div>
  );

  if (isPendingCreation) {
    // The card exists before the provider is asked, so the image renders with
    // masked digits while creation runs.
    return renderWaitingState(
      <CreationState
        creationFailure={creationFailure}
        isStatusPollExhausted={isStatusPollExhausted}
        knownProviderErrors={knownProviderErrors}
      />,
    );
  }

  if (status !== 'active') {
    return (
      <div className={styles.container}>
        <CancelledCard />
      </div>
    );
  }

  const isFailedFunding = fundingStatus === 'failed';

  if (isPendingFunding || isFailedFunding) {
    // The card is active, so show its details: the visible change from the
    // creation state tells the recipient creation succeeded. The funding
    // state sits beneath, in place of the balance panel: a spinner while
    // polling, and check-back-later copy once funding has failed or the poll
    // has given up.
    const getFundingState = () => {
      if (isFailedFunding) {
        return (
          <Heading4>{dictionary.prepaidCards.funding.failedMessage}</Heading4>
        );
      }

      if (isStatusPollExhausted) {
        return (
          <Heading4>{dictionary.prepaidCards.funding.delayedMessage}</Heading4>
        );
      }

      return (
        <>
          <Loader />
          <Heading4>{dictionary.prepaidCards.funding.loadingMessage}</Heading4>
        </>
      );
    };

    return renderWaitingState(getFundingState());
  }

  return (
    <div className={styles.container}>
      <CardImage
        cardData={cardData}
        cardTitle={cardTitle}
        design={cardDesign}
        isHeatingCard={isHeatingCard}
      />

      <CardRestrictionDetails
        helpLinkToShow={helpLinkToShow}
        isHeatingCard={isHeatingCard}
      />

      <CardInformation
        cardTitle={cardTitle}
        cardExpiryDate={cardData.expiryDate}
        cardDetailsMessage={cardDetailsMessage}
        availableBalance={availableBalance}
        spentAmount={spentAmount}
        fetchSensitiveCardData={fetchSensitiveCardData}
        sensitiveCardData={sensitiveCardData}
        isFetchingSensitiveCardData={isFetchingSensitiveCardData}
        fetchSensitiveCardDataError={fetchSensitiveCardDataError}
        reportMisuse={reportMisuse}
        isReportingMisuse={isReportingMisuse}
        reportMisuseError={reportMisuseError}
      />
    </div>
  );
};

PrepaidCardUnconnected.propTypes = {
  id: PropTypes.string.isRequired,
  cardData: PropTypes.object,
  cardStatus: PropTypes.object,
  isStatusPollExhausted: PropTypes.bool,
  startStatusPolling: PropTypes.func.isRequired,
  stopStatusPolling: PropTypes.func.isRequired,
  shortcode: PropTypes.string,
  isFetchingCardData: PropTypes.bool.isRequired,
  fetchCardDataError: PropTypes.object,
  fetchCardData: PropTypes.func.isRequired,
  availableBalance: PropTypes.string.isRequired,
  spentAmount: PropTypes.string.isRequired,
  sensitiveCardData: PropTypes.object,
  isFetchingSensitiveCardData: PropTypes.bool.isRequired,
  fetchSensitiveCardDataError: PropTypes.object,
  fetchSensitiveCardData: PropTypes.func.isRequired,
  reportMisuse: PropTypes.func.isRequired,
  isReportingMisuse: PropTypes.bool.isRequired,
  reportMisuseError: PropTypes.object,
  segment: PropTypes.object,
};

// The live segment's presentation settings, layered over the claim-time
// snapshot, so copy configured after a card was claimed still shows on it.
export const mapStateToProps = (state) => ({
  segment: getEffectiveSegment(state),
  cardData: getCardData(state),
  cardStatus: getCardStatus(state),
  isStatusPollExhausted: getIsStatusPollExhausted(state),
  shortcode: path(['huggg', 'huggg', 'shortcode'], state),
  availableBalance: getAvailableBalanceStr(state),
  spentAmount: getSpentAmountStr(state),
  isFetchingCardData: getIsFetchingCardData(state),
  fetchCardDataError: getFetchCardDataError(state),
  sensitiveCardData: getSensitiveCardData(state),
  isFetchingSensitiveCardData: getIsFetchingSensitiveCardData(state),
  fetchSensitiveCardDataError: getFetchSensitiveCardDataError(state),
  isReportingMisuse: getIsReportingMisuse(state),
  reportMisuseError: getReportMisuseError(state),
  isHeatingCard: isPrepaidHeatingCard(state.huggg),
});

export default compose(
  connect(mapStateToProps, {
    fetchCardData,
    fetchSensitiveCardData,
    reportMisuse,
    startStatusPolling,
    stopStatusPolling,
  }),
)(PrepaidCardUnconnected);
